import React, { useState } from 'react';
import { useExperiments } from '@wix/fe-essentials/react';
import { useTranslation } from '@wix/wix-i18n-config';
import { buildAppBuilderUrl } from '@wix/dev-center-pages-common/deVibe';
import { CLIAppOptionsModal } from '@wix/dev-center-pages-common';
import { shouldDisplayAppBuilderCliTemplatesModal } from '../../../../utils/experiments';
import { useTemplatesData } from '../../../../hooks/useTemplatesData';
import { useCreateAppBuilderApplication } from '../../../../hooks/use-create-app/useCreateAppBuilderApplication';
import { isCliScratchTemplate } from '../../../../utils/templateSelector/templatesSelector';
import { FullScreenLoader } from '../../../FullScreenLoader';
import { GetStartWithCLIModalLayout } from './GetStartWithCLIModalLayout';

export const CLIModalLayout = ({ closeModal }) => {
  const { t } = useTranslation();
  const { experiments } = useExperiments({ readOnly: true });
  const { currentTemplate: template } = useTemplatesData();
  const { createAppBuilderApplication, errorHandler } =
    useCreateAppBuilderApplication();
  const [isCreatingApp, setIsCreatingApp] = useState(false);
  const [mode, setMode] = useState<'getStartedOptions' | 'getStartWithCLI'>(
    () =>
      shouldDisplayAppBuilderCliTemplatesModal(experiments)
        ? 'getStartedOptions'
        : 'getStartWithCLI',
  );
  const createAndOpenAppBuilderApplication = async () => {
    const isScratchTemplate = !!template && isCliScratchTemplate(template);
    const shouldUseTemplate = !!template?.id && !isScratchTemplate;

    try {
      setIsCreatingApp(true);
      const { appId } = await createAppBuilderApplication({
        templateId: shouldUseTemplate ? template.id : undefined,
        appName: shouldUseTemplate ? template.title : undefined,
      });

      window.location.assign(
        buildAppBuilderUrl({
          appId,
          tab: 'ide',
          initialPrompt: '',
          ...(shouldUseTemplate ? { templateId: template.id } : {}),
        }),
      );
    } catch (error) {
      console.error(
        'Failed to create DeVibe app:',
        errorHandler.getResolvedError(error),
      );
      setIsCreatingApp(false);
    }
  };

  const onOptionClick = async (option: 'cli' | 'app-builder') => {
    if (isCreatingApp) {
      return;
    }

    if (option === 'cli') {
      setMode('getStartWithCLI');
      return;
    }

    if (!window.__USER_ID__) {
      const loginUrl = new URL('https://users.wix.com/signin/login');
      loginUrl.searchParams.set('postLogin', window.location.href);
      loginUrl.searchParams.set('postSignUp', window.location.href);
      window.location.href = loginUrl.toString();
      return;
    }

    await createAndOpenAppBuilderApplication();
  };

  if (mode === 'getStartedOptions') {
    return (
      <>
        <CLIAppOptionsModal onClose={closeModal} onNext={onOptionClick} />
        {isCreatingApp && (
          <FullScreenLoader
            label={t('app.templates.self-hosted.creating-app')}
          />
        )}
      </>
    );
  }

  return <GetStartWithCLIModalLayout closeModal={closeModal} />;
};
